export type NextStepType = 'IT_DEMAND' | 'PID_BUSINESS_CASE' | 'NONE';

export type SolutionType = 'IT' | 'NON_DIGITAL';

export type EvaluationOutcome = {
  successful: boolean;
  proceed: boolean;
  comment: string;
  nextStepType: NextStepType;
  solutionType?: SolutionType;
};

export type SuccessCriterionResult = {
  id: string;
  criterion: string;
  measurement: string;
  wasItMet: '' | 'Yes' | 'No';
  howMeasured: string;
  outcomeDescription: string;
};

export function nextStepGuidance(nextStepType: NextStepType): string {
  switch (nextStepType) {
    case 'IT_DEMAND':
      return 'Initiate an IT Demand to begin extended due diligence and formal integration.';
    case 'PID_BUSINESS_CASE':
      return 'Prepare a Project Initiation Document (PID) and Business Case (BC), then proceed with procurement procedures and Security & Enterprise Architecture (which may still require an IT Demand).';
    default:
      return 'No further steps are required. The PoC will be archived.';
  }
}

export function buildOutcome(
  proceedWithImplementation: boolean,
  comment: string,
  solutionType: SolutionType | null
): EvaluationOutcome {
  if (!proceedWithImplementation) {
    return {
      successful: false,
      proceed: false,
      comment,
      nextStepType: 'NONE',
    };
  }

  return {
    successful: true,
    proceed: true,
    comment,
    nextStepType:
      solutionType === 'IT' ? 'IT_DEMAND' : 'PID_BUSINESS_CASE',
    solutionType: solutionType ?? undefined,
  };
}

export function mapApiCriteriaToResults(
  apiRows: {
    id: string;
    description: string;
    evaluationApproach: string;
    measurementMethod: string;
    wasMet?: boolean | null;
    howMeasured?: string | null;
    resultDescription?: string | null;
  }[]
): SuccessCriterionResult[] {
  return apiRows.map((row) => ({
    id: row.id,
    criterion: row.description,
    measurement: row.measurementMethod || row.evaluationApproach,
    wasItMet:
      row.wasMet === true ? 'Yes' : row.wasMet === false ? 'No' : '',
    howMeasured: row.howMeasured ?? '',
    outcomeDescription: row.resultDescription ?? '',
  }));
}

export function isCriteriaComplete(results: SuccessCriterionResult[]): boolean {
  if (results.length === 0) return false;
  return results.every(
    (row) =>
      (row.wasItMet === 'Yes' || row.wasItMet === 'No') &&
      row.howMeasured.trim() &&
      row.outcomeDescription.trim()
  );
}

export function isEvaluationComplete(input: {
  criteriaResults: SuccessCriterionResult[];
  evaluationReportFileName: string | null;
  proceedWithImplementation: boolean | null;
  solutionType: SolutionType | null;
}): boolean {
  if (!isCriteriaComplete(input.criteriaResults)) return false;
  if (!input.evaluationReportFileName) return false;
  if (input.proceedWithImplementation === null) return false;
  if (input.proceedWithImplementation && !input.solutionType) return false;
  return true;
}
